import React from 'react';

import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import { Alert, AlertTitle, Box, Button, Grid } from '@mui/material';

import { DeepPartial } from '@hrlogics/helper-types';
import { Field, Form, FormApi } from '@hrlogics/react-components/form';

import { FEIN_MASK, INDUSTRIES_OPTIONS } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';

import { DEFAULT_AVATAR } from 'src/assets';
import {
  AddressFields,
  AutocompleteField,
  AvatarImageUploadField,
  MaskedTextField,
  PhoneField,
  TextField,
} from 'src/components/finalForm';
import { ModalId } from 'src/constants/modals';
import { useScreenInfo } from 'src/hooks/common';
import { useDemoPrivacyInputSlotProps } from 'src/hooks/demoPrivacyMode';
import { useCurrentUserPermissions } from 'src/hooks/user';
import {
  useVerifierCompanyDataUpdate,
  VerifierCompanyDataUpdateNoticeType,
} from 'src/hooks/verifier';
import { validateWithYupSchema } from 'src/utils/yup';

import * as styles from './styles';
import {
  CommercialVerifierCompanyInformationFormFields,
  CommercialVerifierCompanyInformationFormValues,
} from './types';
import {
  CommercialVerifierCompanyInformationFormValidationSchema,
  getCommercialVerifierInitialValues,
} from './utils';

type CommercialVerifierCompanyInformationFormProps = {
  verifierId: Nullable<string>;
};

export const CommercialVerifierCompanyInformationForm: React.FC<
  CommercialVerifierCompanyInformationFormProps
> = ({ verifierId }) => {
  const { hasPlatformRoles } = useCurrentUserPermissions();
  const { isMobile } = useScreenInfo();
  const demoPrivacyInputSlotProps = useDemoPrivacyInputSlotProps();
  const { verifier, handleUpdate, getVerifierUpdateNoticeType } =
    useVerifierCompanyDataUpdate<CommercialVerifierCompanyInformationFormValues>(verifierId, {
      credentialStatusNotice: ModalId.VerifierCredentialStatusNoticeModal,
      recredentialingNotice: ModalId.UndergoReverificationConfirmationModal,
    });

  const initialValues: CommercialVerifierCompanyInformationFormValues = React.useMemo(
    () => getCommercialVerifierInitialValues(verifier),
    [verifier],
  );

  const onSubmit = React.useCallback(
    async (
      formData: CommercialVerifierCompanyInformationFormValues,
      form: FormApi<
        CommercialVerifierCompanyInformationFormValues,
        DeepPartial<CommercialVerifierCompanyInformationFormValues>
      >,
    ) => {
      try {
        await handleUpdate(formData, form);
      } catch (error) {
        console.error(error);
      }
    },
    [handleUpdate],
  );

  const xsColumn = React.useMemo(() => {
    if (isMobile) return 12;
    if (hasPlatformRoles) return 6;
    return 4;
  }, [hasPlatformRoles, isMobile]);

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      subscription={{ valid: true, values: true, submitting: true, pristine: true, touched: true }}
      containerProps={{ p: isMobile ? 3 : 0 }}
      validate={validateWithYupSchema(CommercialVerifierCompanyInformationFormValidationSchema)}
    >
      {({ submitting, valid, form, pristine }) => {
        const noticeType = getVerifierUpdateNoticeType(form);
        const isNoticeAlertVisible = noticeType !== VerifierCompanyDataUpdateNoticeType.none;

        return (
          <Grid container spacing={2}>
            {!pristine && isNoticeAlertVisible && renderCredentialStatusAlert(noticeType)}
            {isMobile && (
              <Grid display="flex" alignItems="center" justifyContent="center" size={12}>
                <Field
                  name={CommercialVerifierCompanyInformationFormFields.logoFile}
                  component={AvatarImageUploadField}
                  defaultImageUrl={verifier?.logo?.downloadUrl || DEFAULT_AVATAR}
                  avatarProps={{ sx: styles.avatarStyles }}
                  disabled={submitting}
                />
              </Grid>
            )}
            <Grid size={isMobile ? 12 : 6}>
              <Box display="flex" flexDirection="column" gap={2}>
                <Field
                  label="Company Display Name"
                  name={CommercialVerifierCompanyInformationFormFields.displayName}
                  fullWidth
                  required
                  disabled={submitting}
                  component={TextField}
                  slotProps={demoPrivacyInputSlotProps}
                />
                <Field
                  label="Commercial Name"
                  name={CommercialVerifierCompanyInformationFormFields.commercialName}
                  fullWidth
                  disabled={submitting}
                  component={TextField}
                  slotProps={demoPrivacyInputSlotProps}
                />
              </Box>
            </Grid>
            <Grid size={xsColumn}>
              <Box display="flex" flexDirection="column" gap={2}>
                <Field
                  label="Registered Business Name"
                  name={CommercialVerifierCompanyInformationFormFields.legalBusinessName}
                  fullWidth
                  required
                  disabled={submitting}
                  component={TextField}
                  slotProps={demoPrivacyInputSlotProps}
                />
                {!isMobile && (
                  <Field
                    fullWidth
                    label="FEIN"
                    name={CommercialVerifierCompanyInformationFormFields.fEIN}
                    component={MaskedTextField}
                    mask={FEIN_MASK}
                    required
                    disabled={submitting}
                    slotProps={demoPrivacyInputSlotProps}
                  />
                )}
              </Box>
            </Grid>
            {!isMobile && (
              <React.Fragment>
                {!hasPlatformRoles && (
                  <Grid display="flex" alignItems="center" justifyContent="center" size={2}>
                    <Field
                      name={CommercialVerifierCompanyInformationFormFields.logoFile}
                      component={AvatarImageUploadField}
                      defaultImageUrl={verifier?.logo?.downloadUrl || DEFAULT_AVATAR}
                      avatarProps={{ sx: styles.avatarStyles }}
                      disabled={submitting}
                    />
                  </Grid>
                )}
                <Grid size={12}>
                  <Field
                    label="Industry"
                    name={CommercialVerifierCompanyInformationFormFields.industry}
                    fullWidth
                    required
                    disabled={submitting}
                    options={INDUSTRIES_OPTIONS}
                    component={AutocompleteField}
                  />
                </Grid>
              </React.Fragment>
            )}
            {isMobile && (
              <Grid container spacing={2} size={12}>
                <Grid size={6}>
                  <Field
                    label="Primary Contact Phone"
                    name={CommercialVerifierCompanyInformationFormFields.contactPhone}
                    fullWidth
                    required
                    disabled={submitting}
                    component={PhoneField}
                    slotProps={demoPrivacyInputSlotProps}
                  />
                </Grid>
                <Grid size={6}>
                  <Field
                    fullWidth
                    label="FEIN"
                    name={CommercialVerifierCompanyInformationFormFields.fEIN}
                    component={MaskedTextField}
                    mask={FEIN_MASK}
                    required
                    disabled={submitting}
                    slotProps={demoPrivacyInputSlotProps}
                  />
                </Grid>
              </Grid>
            )}
            <Grid size={isMobile ? 12 : 6}>
              {!isMobile ? (
                <Field
                  label="Primary Contact Phone"
                  name={CommercialVerifierCompanyInformationFormFields.contactPhone}
                  fullWidth
                  required
                  disabled={submitting}
                  component={PhoneField}
                  slotProps={demoPrivacyInputSlotProps}
                />
              ) : (
                <Field
                  label="Industry"
                  name={CommercialVerifierCompanyInformationFormFields.industry}
                  fullWidth
                  required
                  disabled={submitting}
                  options={INDUSTRIES_OPTIONS}
                  component={AutocompleteField}
                />
              )}
            </Grid>
            <Grid size={isMobile ? 12 : 6}>
              <Field
                label="Primary Contact Email"
                name={CommercialVerifierCompanyInformationFormFields.contactEmail}
                fullWidth
                required
                disabled={submitting}
                component={TextField}
                slotProps={demoPrivacyInputSlotProps}
              />
            </Grid>
            <Grid size={12}>
              <AddressFields
                prefix={CommercialVerifierCompanyInformationFormFields.address}
                disabled={submitting}
                required
                inputSlotProps={demoPrivacyInputSlotProps}
              />
            </Grid>
            <Grid size={12}>
              <Field
                fullWidth
                name={CommercialVerifierCompanyInformationFormFields.websiteURL}
                label="Website URL"
                component={TextField}
                disabled={submitting}
                required
                slotProps={demoPrivacyInputSlotProps}
              />
            </Grid>
            <Grid size={12}>
              <Box display="flex" justifyContent="center" gap={2} mt={2} mb={4}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => form.reset()}
                  disabled={submitting || pristine}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!valid || submitting || pristine}
                  loading={submitting}
                >
                  Save changes
                </Button>
              </Box>
            </Grid>
          </Grid>
        );
      }}
    </Form>
  );
};

const renderCredentialStatusAlert = (noticeType: string) => {
  switch (noticeType) {
    case VerifierCompanyDataUpdateNoticeType.credentialStatusNotice: {
      return (
        <Grid sx={{ my: '8px' }} size={12}>
          <Alert
            icon={<InfoOutlinedIcon fontSize="inherit" />}
            severity="warning"
            variant="outlined"
          >
            <AlertTitle>Credential Status Notice</AlertTitle>
            Your company has not been approved to create verification requests yet. Any changes made
            to your company information can influence the credentialing process.
          </Alert>
        </Grid>
      );
    }

    case VerifierCompanyDataUpdateNoticeType.recredentialingNotice: {
      return (
        <Grid sx={{ my: '8px' }} size={12}>
          <Alert
            icon={<InfoOutlinedIcon fontSize="inherit" />}
            severity="warning"
            variant="outlined"
          >
            <AlertTitle>Recredentialing Notice</AlertTitle>
            Any changes made to your company information will require your account to get
            recredentialed before any verification requests are allowed.
          </Alert>
        </Grid>
      );
    }

    default: {
      return null;
    }
  }
};
